/**
 * Dates are rendered with a NAMED month. Always.
 *
 * A bare `toLocaleDateString()` formats to whatever locale the viewer's browser
 * is set to, so the same row reads `09/14/1989` on one machine and `14/09/1989`
 * on another — with nothing on screen saying which. For most columns that is an
 * annoyance. For a birth date it is the difference between two different charts,
 * and the mistake is invisible: both readings are valid dates.
 *
 * `formatDate` removes the ambiguity by never printing a numeric month.
 */

/**
 * A date-only string (`1989-09-14`) is parsed by `new Date()` as UTC midnight,
 * so in any timezone behind UTC it renders as the day before. Build those as a
 * local date instead; anything with a time in it is a real instant and is left
 * alone.
 */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  if (typeof value === "string") {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnly) {
      return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    }
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `14 Sep 1989`. Unreadable as anything else, in any locale. */
export function formatDate(
  value: string | number | Date | null | undefined,
  fallback = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** `14 September 1989` — for the one-off places that have room for it. */
export function formatDateLong(
  value: string | number | Date | null | undefined,
  fallback = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/** `14 Sep 1989, 14:32`. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  fallback = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  return `${formatDate(d)}, ${d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

/**
 * `09 Sep 2026, 06:26:09 IST` — the exact moment a chart was cast for.
 *
 * TWO THINGS THIS FIXES, AND BOTH WERE REPORTED FROM OUTSIDE.
 *
 * SECONDS. `formatDateTime` renders hour and minute, so an event stored at
 * 06:26:09 displayed as "06:26". An astrologer re-casting from the screen
 * therefore starts up to 59 seconds out — and sixty seconds moves every cusp
 * about a quarter of a degree and changed FOUR of twelve cusp sub-lords on
 * the chart this was found on. For a KP chart the seconds are not decoration.
 *
 * THE ZONE. An event carries its own timezone and the moment was rendered in
 * the BROWSER's, which is a different civil time for anyone not sitting in the
 * same zone as the event. Both charts here are IST, so it has not bitten yet;
 * it would the first time somebody opens a Mumbai event from abroad.
 *
 * Falls back to the browser's zone when the event has none, rather than
 * guessing one — and says so by omitting the suffix.
 */
export function formatChartMoment(
  value: string | number | Date | null | undefined,
  timeZone?: string | null,
  fallback = "—",
): string {
  const d = toDate(value);
  if (!d) return fallback;
  const opts: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    ...(timeZone ? { timeZone, timeZoneName: "short" } : {}),
  };
  // en-GB gives `09 Sep 2026, 06:26:09` — the same shape formatDate produces,
  // so the two read as one family rather than two date formats on one screen.
  return d.toLocaleString("en-GB", opts);
}

